import { config } from "assets/config/config";
import slug from "slug";
import { cleanDescription } from "utils/headUtil";
import {
  getOrganizationName,
  getShopName,
  HOMEPAGE_SEO,
  normalizeLang,
  PERSON_DEFAULT_JOB,
  pickLocalized,
  PRODUCT_PROPERTY_LABELS,
} from "utils/seo/i18n";
import { buildProductOfferSchema } from "utils/seo/productPrice";

export const getHeadStructureHomepage = (lang = "pl") => {
  const baseUrl = config.APP_MAIN_URL;
  const locale = normalizeLang(lang);
  const homepage = pickLocalized(locale, HOMEPAGE_SEO);
  const pageUrl = `${baseUrl}/${locale}`;

  return ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": getOrganizationName(locale),
        "url": "https://nifc.pl#organization",
        "logo": `${baseUrl}/favicon/android-icon-192x192.png`,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Tamka 43",
          "addressLocality": "Warszawa",
          "postalCode": "00-355",
          "addressCountry": "PL"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+48 22 44 16 100",
          "contactType": "Customer Service",
          "areaServed": "PL",
          "availableLanguage": ["Polish", "English"]
        },
        "sameAs": [
          config.SOCIAL_FACEBOOK_URL,
          config.SOCIAL_X_URL,
          config.SOCIAL_YOUTUBE_URL
        ]
      },
      {
        "@type": "WebSite",
        "name": getShopName(locale),
        "url": pageUrl,
        "inLanguage": locale === "en" ? "en-US" : "pl-PL",
        "publisher": {
          "@id": "https://nifc.pl/#organization"
        }
      },
      {
        "@type": "LocalBusiness",
        "name": homepage.localBusinessName,
        "image": `${baseUrl}/favicon/og_image.jpg`,
        "description": homepage.localBusinessDescription,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "ul. Okólnik 1",
          "addressLocality": "Warszawa",
          "postalCode": "00-368",
          "addressCountry": "PL"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "52.2365683819823",
          "longitude": "21.022936966798625"
        },
        "url": pageUrl,
        "telephone": "+48 22 44 16 205",
        "email": "sales@nifc.pl",
        "openingHours": "Tu-Su 10:00-18:00",
        "priceRange": "$"
      }
    ]
  });
};

export const getHeadStructureProduct = (product, lang = 'pl', currency = 'PLN') => {
  const baseUrl = config.APP_MAIN_URL;

  let nameForSlug = product.name || "";
  if (product.number && nameForSlug.includes(product.number)) {
    nameForSlug = nameForSlug.replace(product.number, "").trim();
  }

  const productSlug = nameForSlug ? slug(nameForSlug) : '';
  const productUrl = `${baseUrl}/${lang}/produkt/${product.id}-${productSlug}`;

  const rawDescription = product.description || product.short_description || "";
  const description = cleanDescription(rawDescription)

  const itemsIsObject = product.items && !Array.isArray(product.items);

  const groupId = Number(product.id_group);
  const isBook = [1, 14, 18, 23].includes(groupId);

  const mainStructure = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "image": product.thumbnail_image,
    "description": description || product.name,
    "sku": product.number || `NIFC-${product.id}`,
    "url": productUrl,
    "brand": {
      "@type": "Organization",
      "name": getOrganizationName(lang)
    },
    "offers": {
      ...buildProductOfferSchema(product, currency, productUrl),
      itemCondition: "https://schema.org/NewCondition",
    }
  };

  if (product.isbn || (itemsIsObject && product.items.isbn)) {
    mainStructure.isbn = product.isbn || product.items.isbn;
  }

  if (isBook) {
    let authorName = "";
    if (product.author && product.author[0]) {
      const a = product.author[0];
      authorName = a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : (a.name || "");
    }
    const finalAuthor = authorName || (itemsIsObject ? product.items.entry : "");

    if (finalAuthor) {
      mainStructure.author = {
        "@type": "Person",
        "name": finalAuthor
      };
    }
  }

  const additionalProps = [];
  if (product.material) {
    additionalProps.push({
      "@type": "PropertyValue",
      "name": pickLocalized(lang, PRODUCT_PROPERTY_LABELS.material),
      "value": product.material,
    });
  }
  if (product.color_options) {
    additionalProps.push({
      "@type": "PropertyValue",
      "name": pickLocalized(lang, PRODUCT_PROPERTY_LABELS.color),
      "value": product.color_options,
    });
  }
  if (product.capacity) {
    additionalProps.push({
      "@type": "PropertyValue",
      "name": pickLocalized(lang, PRODUCT_PROPERTY_LABELS.capacity),
      "value": product.capacity,
    });
  }

  const pages = product.pages_number || (itemsIsObject ? product.items.pages_num : null);
  if (pages) {
    additionalProps.push({
      "@type": "PropertyValue",
      "name": pickLocalized(lang, PRODUCT_PROPERTY_LABELS.pages),
      "value": pages,
    });
  }

  if (additionalProps.length > 0) {
    mainStructure.additionalProperty = additionalProps;
  }

  return mainStructure;
};

export const getHeadStructureProductList = (data, lang = 'pl', currentPath = '', currency = 'PLN') => {
  const baseUrl = config.APP_MAIN_URL;
  const items = data.items || [];
  const meta = data._meta || {};
  const group = data.group || data.category?.group || {};
  const category = data.category || {};

  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const categoryUrl = `${baseUrl}${cleanPath}`;

  const shopName = getShopName(lang);
  const pageSuffix = meta.currentPage > 1
    ? (lang === 'pl' ? ` (Strona ${meta.currentPage})` : ` (Page ${meta.currentPage})`)
    : "";

  let listName = group.name || category.name;
  let listDescription = group.seo_description || category.seo_description;

  if (currentPath.includes('/promocje')) {
    listName = lang === 'pl' ? "Promocje" : "Special Offers";
    listDescription = lang === 'pl' ? "Oferty specjalne i promocje." : "Special offers and promotions.";
  } else if (currentPath.includes('/wyszukiwanie')) {
    listName = lang === 'pl' ? "Wyniki wyszukiwania" : "Search Results";
    listDescription = lang === 'pl' ? "Wyniki wyszukiwania w sklepie NIFC." : "NIFC Store search results.";
  } else {
    listName = listName || (lang === 'pl' ? "Produkty" : "Products");
    listDescription = listDescription || (lang === 'pl' ? `Kategoria: ${listName}` : `Category: ${listName}`);
  }

  if (!listName.includes(shopName)) {
    listName = `${listName} - ${shopName}`;
  }

  const finalListName = `${listName}${pageSuffix}`;

  const itemListElement = items.map((product, index) => {
    let nameForSlug = product.name || "";
    if (product.number && nameForSlug.includes(product.number)) {
      nameForSlug = nameForSlug.replace(product.number, "").trim();
    }
    const productSlug = nameForSlug ? slug(nameForSlug) : '';
    const productUrl = `${baseUrl}/${lang}/produkt/${product.id}-${productSlug}`;

    const groupId = Number(product.id_group);
    const catalogNumber = (product.number || "").toUpperCase();
    const hasAudios = !!(product.audios && product.audios.length > 0);

    let specificType = "Product";
    if (catalogNumber.includes("NIFCDVD") || catalogNumber.includes("NIFCBLU")) {
      specificType = "Movie";
    } else if (groupId === 2 || hasAudios || catalogNumber.includes("NIFCCD")) {
      specificType = "MusicAlbum";
    } else if ([1, 14, 18, 23].includes(groupId)) {
      specificType = "Book";
    }

    const itemSchema = {
      "@type": specificType,
      "url": productUrl,
      "name": product.name,
      "image": product.thumbnail_image,
      "sku": product.number || `NIFC-${product.id}`,
      "offers": buildProductOfferSchema(product, currency, productUrl)
    };

    if (specificType === "MusicAlbum") {
      if (product.performer) itemSchema.byArtist = { "@type": "MusicGroup", "name": product.performer };
      if (product.composer) itemSchema.composer = { "@type": "Person", "name": product.composer };
    }

    if (specificType === "Book") {
      const itemsIsObject = product.items && !Array.isArray(product.items);
      const isbn = product.isbn || (itemsIsObject ? product.items.isbn : null);
      if (isbn) itemSchema.isbn = isbn;
    }

    return {
      "@type": "ListItem",
      "position": (meta.currentPage - 1) * meta.perPage + (index + 1),
      "item": itemSchema
    };
  });

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": finalListName,
    "description": listDescription,
    "url": categoryUrl,
    "numberOfItems": meta.totalCount || items.length,
    "itemListElement": itemListElement
  };
};

export const getHeadStructurePeopleList = (data, lang = 'pl', currentPath = '', type = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const items = data || {};
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;

  const shopName = getShopName(lang);

  const translations = {
    artysci: { pl: "Artyści", en: "Artists", schemaType: "Person" },
    autorzy: { pl: "Autorzy", en: "Authors", schemaType: "Person" },
    "autorzy-redaktorzy": { pl: "Autorzy i Redaktorzy", en: "Authors & Editors", schemaType: "Person" },
    dyrygenci: { pl: "Dyrygenci", en: "Conductors", schemaType: "Person" },
    instytucje: { pl: "Instytucje i Zespoły", en: "Institutions & Ensembles", schemaType: "MusicGroup" }
  };

  const configType = translations[type] || translations['artysci'];
  const typeName = pickLocalized(lang, configType);
  const schemaType = configType.schemaType;

  let listTitle = typeName;
  if (!listTitle.includes(shopName)) {
    listTitle = `${listTitle} - ${shopName}`;
  }

  const allPeople = [];
  let currentIndex = 1;

  Object.keys(items).sort().forEach(letter => {
    if (letter.length <= 2 && Array.isArray(items[letter])) {
      items[letter].forEach(person => {
        const fullName = person.title || person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim();
        const baseForSlug = person.title || person.last_name || person.first_name || 'item';
        const itemSlug = slug(baseForSlug);

        const itemSchema = {
          "@type": "ListItem",
          "position": currentIndex++,
          "item": {
            "@type": schemaType,
            "name": fullName,
            "url": `${baseUrl}/${lang}/osoba/${person.id}-${itemSlug}`
          }
        };

        if (schemaType === "Person") {
          itemSchema.item.jobTitle = typeName;
        }

        if (person.image && person.image.trim() !== "") {
          itemSchema.item.image = person.image;
        }

        allPeople.push(itemSchema);
      });
    }
  });

  return {
    "@context": "https://schema.org",
    "@type": "ItemPage",
    "mainEntity": {
      "@type": "ItemList",
      "name": listTitle,
      "description": lang === 'pl' ? `Oficjalna lista: ${typeName}.` : `Official list: ${typeName}.`,
      "url": pageUrl,
      "numberOfItems": allPeople.length,
      "itemListElement": allPeople
    }
  };
};

export const getHeadStructurePersonDetail = (data, lang = 'pl', currentPath = '', currency = 'PLN') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;

  const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim();

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": fullName,
    "url": pageUrl,
    "jobTitle": data.category || pickLocalized(lang, PERSON_DEFAULT_JOB),
    "description": cleanDescription(data.lead || data.biography).substring(0, 200),
    "birthDate": data.date_of_born || null,
    "deathDate": data.date_of_die || null,
    "nationality": {
      "@type": "Country",
      "name": data.country_name,
      "identifier": data.iso_country
    }
  };

  if (data.image && data.image.trim() !== "") {
    personSchema.image = data.image;
  }

  if (data.products && data.products.length > 0) {
    personSchema.mainEntityOfPage = {
      "@type": "ItemList",
      "name": lang === 'pl' ? `Produkty powiązane z: ${fullName}` : `Products related to: ${fullName}`,
      "numberOfItems": data.products.length,
      "itemListElement": data.products.map((product, index) => {
        const productSlug = product.name ? slug(product.name) : '';
        const productUrl = `${baseUrl}/${lang}/produkt/${product.id}-${productSlug}`;
        return {
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Product",
            "name": product.name,
            "image": product.thumbnail_image,
            "url": productUrl,
            "offers": buildProductOfferSchema(product, currency, productUrl)
          }
        };
      })
    };
  }

  return personSchema;
};

export const getHeadStructureTextPage = (data, lang = 'pl', currentPath = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;
  const shopName = getShopName(lang);

  const title = (data && data.title) || (lang === 'pl' ? "Informacje" : "Information");
  const description = cleanDescription((data && data.text) || "").substring(0, 200);

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "headline": title,
    "description": description,
    "url": pageUrl,
    "inLanguage": lang === 'en' ? "en-US" : "pl-PL",
    "isPartOf": {
      "@type": "WebSite",
      "name": shopName,
      "url": baseUrl
    }
  };
};

export const getHeadStructureInstitutionDetail = (data, lang = 'pl', currentPath = '', currency = 'PLN') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;

  const title = data.title || "";

  const institutionSchema = {
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    "name": title,
    "url": pageUrl,
    "description": cleanDescription(data.text).substring(0, 200),
    "image": data.image || null
  };

  if (data.products && data.products.length > 0) {
    institutionSchema.mainEntityOfPage = {
      "@type": "ItemList",
      "name": lang === 'pl' ? `Nagrania: ${title}` : `Recordings by: ${title}`,
      "numberOfItems": data.products.length,
      "itemListElement": data.products.map((product, index) => {
        const productSlug = slug(product.name || 'product');
        const productUrl = `${baseUrl}/${lang}/produkt/${product.id}-${productSlug}`;
        return {
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Product",
            "name": product.name,
            "image": product.thumbnail_image,
            "url": productUrl,
            "offers": buildProductOfferSchema(product, currency, productUrl)
          }
        };
      })
    };
  }

  return institutionSchema;
};